import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Search, ArrowLeft, WalletCards } from "lucide-react";

import { makeRequest } from "@/utils/makeRequest";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useProfile, useSystemSettings } from "@/hooks";
import Loader from "@/components/loader/Loader";
import { useAppSelector } from "@/store/hooks";
import { useNavigate } from "react-router-dom";
import type {
  FormattedTransaction,
  FormattedTransactionInterface,
} from "@/interfaces/transaction";
import TransactionReceipt from "@/components/user/TransactionReceipt";
import { copyToClipboard } from "@/utils";
import { ErrorState } from "@/components/ErrorState";
import { TransactionRow } from "@/components/user/TransactionRow";

export default function UserHistory() {
  const { user } = useAppSelector((state) => state.auth);
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [selectedTx, setSelectedTx] = useState<FormattedTransaction | null>(
    null,
  );
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data: profile, isLoading: isLoadingProfile } = useProfile(
    user?.id || "",
  );

  const { data: systemSettings } = useSystemSettings();

  const fetchTransactions = async () => {
    const res = await makeRequest.get("/transaction/me", {
      params: { page, limit: 10 },
    });
    console.log("Fetched transactions:", res.data);
    return res.data;
  };

  const {
    data: userTransactions,
    isLoading,
    isError,
    refetch,
  } = useQuery<FormattedTransactionInterface>({
    queryKey: ["transactions", user?.id, page],
    queryFn: fetchTransactions,
    enabled: !!user?.id,
  });

  console.log("TRANSACTIONS", userTransactions);

  const transactions: FormattedTransaction[] = userTransactions?.data ?? [];
  const meta = userTransactions?.meta;

  const filteredTransactions = useMemo(() => {
    const searchTerm = search.toLowerCase().trim();
    return transactions.filter((tx) => {
      const matchesSearch =
        !searchTerm ||
        tx.id.toLowerCase().includes(searchTerm) ||
        tx.message?.toLowerCase().includes(searchTerm) ||
        tx.counterparty.name.toLowerCase().includes(searchTerm);

      const matchesType = typeFilter === "all" || tx.type === typeFilter;
      const matchesStatus =
        statusFilter === "all" || tx.status === statusFilter;

      return matchesSearch && matchesType && matchesStatus;
    });
  }, [transactions, search, typeFilter, statusFilter]);

  const openReceipt = (tx: FormattedTransaction) => {
    setSelectedTx(tx);
    setIsModalOpen(true);
  };

  if (isLoading || isLoadingProfile) {
    return <Loader type="bounce" />;
  }

  if (isError) {
    return (
      <ErrorState
        onRetry={() => {
          refetch();
        }}
      />
    );
  }

  return (
    <div className="space-y-8 pb-12">
      {/* Header */}
      <div>
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-4 -ml-2 hidden md:flex"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
      </div>

      {/* Filters */}
      <div className="shadow-sm">
        <div className="p-1 sm:p-6">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            {/* SEARCH */}
            <div className="relative flex-1 w-full">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by ID, message or counterparty..."
                className="pl-11 h-14 text-base w-full focus-visible:ring-2 focus-visible:ring-primary/30"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            {/* FILTER GROUP */}
            <div className="flex item-center gap-3 w-full lg:w-auto">
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-full sm:w-[180px] h-14 flex items-center justify-between px-3 leading-none">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="TRANSFER">Transfer</SelectItem>
                  <SelectItem value="SWAP">Swap</SelectItem>
                  <SelectItem value="TRADE">Trade</SelectItem>
                </SelectContent>
              </Select>

              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-[180px] h-14 flex items-center justify-between px-3 leading-none">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="COMPLETED">Completed</SelectItem>
                  <SelectItem value="PENDING">Pending</SelectItem>
                  <SelectItem value="FAILED">Failed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      {/* Deposit completion information */}
      <div className="rounded-xl border border-primary/20 bg-primary/[0.04] px-4 py-4 sm:px-5">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <WalletCards className="h-4 w-4" />
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <h3 className="text-sm font-semibold text-foreground">
                Deposit completion threshold
              </h3>

              <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                Minimum ${Number(systemSettings?.depositMinUSD)?.toFixed(2)}
              </span>
            </div>

            <p className="mt-1 text-sm leading-5 text-muted-foreground">
              Deposits are aggregated separately for each asset. A deposit can
              only be marked as completed once your total confirmed deposit for
              that asset reaches the minimum threshold of{" "}
              <span className="font-medium text-foreground">
                ${Number(systemSettings?.depositMinUSD)?.toFixed(2)}
              </span>{" "}
              equivalent.
            </p>
          </div>
        </div>
      </div>

      {/* Transactions Card */}
      <Card className="shadow-sm overflow-hidden">
        <CardContent className="p-0">
          <Tabs defaultValue="all">
            <TabsList className="w-full justify-start rounded-none border-b bg-transparent px-6">
              <TabsTrigger value="all" className="py-3">
                All
              </TabsTrigger>
              <TabsTrigger value="in" className="py-3">
                Deposits
              </TabsTrigger>
              <TabsTrigger value="out" className="py-3">
                Withdrawals
              </TabsTrigger>
            </TabsList>

            {/* ALL TRANSACTIONS */}
            <TabsContent value="all" className="m-0">
              <div className="divide-y">
                {filteredTransactions.length > 0 ? (
                  filteredTransactions.map((tx) => (
                    <TransactionRow
                      key={tx.id}
                      tx={tx}
                      onClick={() => openReceipt(tx)}
                      onCopy={(e) => {
                        e.stopPropagation();
                        copyToClipboard(tx.id);
                      }}
                    />
                  ))
                ) : (
                  <div className="p-12 text-center text-muted-foreground">
                    No transactions found matching your criteria.
                  </div>
                )}
              </div>
            </TabsContent>

            {/* DEPOSITS (IN) */}
            <TabsContent value="in" className="m-0">
              <div className="divide-y">
                {filteredTransactions.filter((tx) => tx.direction === "IN")
                  .length > 0 ? (
                  filteredTransactions
                    .filter((tx) => tx.direction === "IN")
                    .map((tx) => (
                      <TransactionRow
                        key={tx.id}
                        tx={tx}
                        onClick={() => openReceipt(tx)}
                        onCopy={(e) => {
                          e.stopPropagation();
                          copyToClipboard(tx.id);
                        }}
                      />
                    ))
                ) : (
                  <div className="p-12 text-center text-muted-foreground">
                    No deposits found.
                  </div>
                )}
              </div>
            </TabsContent>

            {/* WITHDRAWALS (OUT) */}
            <TabsContent value="out" className="m-0">
              <div className="divide-y">
                {filteredTransactions.filter((tx) => tx.direction === "OUT")
                  .length > 0 ? (
                  filteredTransactions
                    .filter((tx) => tx.direction === "OUT")
                    .map((tx) => (
                      <TransactionRow
                        key={tx.id}
                        tx={tx}
                        onClick={() => openReceipt(tx)}
                        onCopy={(e) => {
                          e.stopPropagation();
                          copyToClipboard(tx.id);
                        }}
                      />
                    ))
                ) : (
                  <div className="p-12 text-center text-muted-foreground">
                    No withdrawals found.
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>

          {/* Pagination */}
          {meta && (
            <div className="flex items-center justify-between border-t px-6 py-5">
              <Button
                variant="outline"
                disabled={!meta.hasPrevPage}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </Button>

              <p className="text-sm text-muted-foreground">
                Page <span className="font-medium">{meta.page}</span> of{" "}
                {meta.totalPages}
              </p>

              <Button
                variant="outline"
                disabled={!meta.hasNextPage}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <TransactionReceipt
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        selectedTx={selectedTx}
      />
    </div>
  );
}
